import { useEffect, useRef, useState } from 'react'

import { zoomOf } from 'utils/textZoom'

// VERA-914 (Stage B) — extracted from ProductEditPage's inline scroll-spy /
// smooth-scroll logic so the Create/Edit Owner sectioned form can reuse the
// exact same TOC behavior without duplicating it twice. ProductEditPage itself
// is left untouched (lower risk) — this is a straight parametrized copy.
export const useSectionScrollSpy = (sectionIds: string[], ready: boolean) => {
  const [activeId, setActiveId] = useState<string>(sectionIds[0])
  // After an explicit TOC click we pin the active section and ignore scroll-spy
  // until the user scrolls by hand.
  const suppressSpyRef = useRef(false)

  // Nearest scrollable ancestor (the app scrolls inside <main>, not window).
  const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let el = node?.parentElement ?? null
    while (el) {
      const oy = getComputedStyle(el).overflowY
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
        return el
      }
      el = el.parentElement
    }
    return null
  }

  useEffect(() => {
    if (!ready) return
    const first = sectionIds.map((id) => document.getElementById(id)).find(Boolean)
    const sc = getScrollParent((first as HTMLElement) ?? null)
    if (!sc) return

    const onScroll = () => {
      if (suppressSpyRef.current) return
      const present = sectionIds.filter((id) => document.getElementById(id))
      if (!present.length) return
      // At the bottom, force the last section active (it may never reach the top).
      if (sc.scrollTop + sc.clientHeight >= sc.scrollHeight - 4) {
        setActiveId(present[present.length - 1])
        return
      }
      const scTop = sc.getBoundingClientRect().top
      // 100 is layout px; the rect delta is painted px (× the text zoom — see `zoomOf`).
      const scZoom = zoomOf(sc)
      let current = present[0]
      for (const id of present) {
        const el = document.getElementById(id)!
        if (el.getBoundingClientRect().top - scTop <= 100 * scZoom) current = id
      }
      setActiveId(current)
    }

    const releaseSpy = () => {
      suppressSpyRef.current = false
    }

    sc.addEventListener('scroll', onScroll, { passive: true })
    sc.addEventListener('wheel', releaseSpy, { passive: true })
    sc.addEventListener('touchmove', releaseSpy, { passive: true })
    sc.addEventListener('keydown', releaseSpy)
    onScroll()
    return () => {
      sc.removeEventListener('scroll', onScroll)
      sc.removeEventListener('wheel', releaseSpy)
      sc.removeEventListener('touchmove', releaseSpy)
      sc.removeEventListener('keydown', releaseSpy)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, sectionIds.join('|')])

  const scrollToSection = (sectionId: string) => {
    suppressSpyRef.current = true
    setActiveId(sectionId)
    const el = document.getElementById(sectionId)
    if (!el) return
    const sc = getScrollParent(el)
    if (!sc) {
      el.scrollIntoView({ block: 'start', behavior: 'smooth' })
      return
    }
    // If a sticky top nav overlays the scroll-container top (small screens), land
    // the section just below it.
    let stickyOffset = 0
    for (
      let n = document.querySelector<HTMLElement>('[data-section-id]');
      n && n !== sc;
      n = n.parentElement
    ) {
      if (getComputedStyle(n).position === 'sticky') {
        stickyOffset = Math.max(0, n.getBoundingClientRect().bottom - sc.getBoundingClientRect().top) / zoomOf(sc)
        break
      }
    }
    // 12px breathing room above the card (plus any sticky-nav height).
    // `scrollTop` is LAYOUT px; rect deltas are PAINTED px (× the text zoom). Both the
    // delta and the sticky offset above are divided back to layout px before they are
    // added to it, or the section lands short by delta × (1 − z) at z < 1.
    const delta = (el.getBoundingClientRect().top - sc.getBoundingClientRect().top) / zoomOf(sc)
    const target = Math.max(
      0,
      Math.min(sc.scrollTop + delta - 12 - stickyOffset, sc.scrollHeight - sc.clientHeight)
    )
    const start = sc.scrollTop
    const change = target - start
    if (Math.abs(change) < 1) return
    const t0 = performance.now()
    const ease = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)
    let done = false
    const step = (now: number) => {
      if (done) return
      const p = Math.min(1, (now - t0) / 320)
      sc.scrollTop = start + change * ease(p)
      if (p < 1) requestAnimationFrame(step)
      else done = true
    }
    requestAnimationFrame(step)
    setTimeout(() => {
      if (!done && sc.scrollTop === start) {
        sc.scrollTop = target
        done = true
      }
    }, 80)
  }

  return { activeId, setActiveId, scrollToSection, suppressSpyRef }
}
